import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Add props to control the header behavior
interface HeaderProps {
  isHomePage?: boolean;
  externalScrollY?: number;
}

function Header({ isHomePage = false, externalScrollY }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [, setScrollY] = useState(0);
  
  // Use either external scrollY (for homepage) or track our own
  useEffect(() => {
    if (typeof externalScrollY === 'number') {
      setScrollY(externalScrollY);
    } else {
      const handleScroll = () => setScrollY(window.scrollY);
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [externalScrollY]);

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  const drawerVariants = {
    hidden: { x: '100%' },
    visible: { x: 0 }
  };

  // Close menu on navigation
  const handleClose = () => setMobileMenuOpen(false);

  return (
    <>
      <header className="flex justify-between items-center px-6 md:px-12 py-6 fixed w-full z-50 bg-white/85 backdrop-blur-md border-b border-ink-line">
        <Link to="/" className="font-display text-xl tracking-tight text-ink hover:opacity-60 transition-opacity">
          TND Labs
        </Link>

        {/* Desktop Navigation - Hidden on mobile */}
        <nav className="hidden md:flex items-center space-x-10">
          <a href={isHomePage ? "#ventures" : "/#ventures"} className="text-xs uppercase tracking-eyebrow text-ink-soft hover:text-ink transition-colors">
            Ventures
          </a>
          <a href={isHomePage ? "#about" : "/#about"} className="text-xs uppercase tracking-eyebrow text-ink-soft hover:text-ink transition-colors">
            About
          </a>
          <a href={isHomePage ? "#contact" : "/#contact"} className="text-xs uppercase tracking-eyebrow text-ink-soft hover:text-ink transition-colors">
            Contact
          </a>
        </nav>

        {/* Hamburger Menu Button */}
        <button
          className="md:hidden flex flex-col justify-center items-center"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <div className={`w-6 h-px bg-ink mb-2 transition-all duration-300 ${mobileMenuOpen ? 'transform rotate-45 translate-y-[9px]' : ''}`}></div>
          <div className={`w-6 h-px bg-ink mb-2 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}></div>
          <div className={`w-6 h-px bg-ink transition-all duration-300 ${mobileMenuOpen ? 'transform -rotate-45 -translate-y-[9px]' : ''}`}></div>
        </button>
      </header>
      
      {/* Mobile Menu with Close Button */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-50"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={overlayVariants}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
            />
            <motion.div
              className="fixed top-0 right-0 h-full w-4/5 max-w-sm bg-white z-50 flex flex-col justify-start overflow-y-auto shadow-xl"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={drawerVariants}
              transition={{ type: "tween", duration: 0.3 }}
            >
              <div className="sticky top-0 flex justify-end p-6 bg-white z-50">
                <button
                  onClick={handleClose}
                  className="text-ink-muted hover:text-ink transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <nav className="flex flex-col items-start space-y-6 px-8 mt-8 pb-24">
                <a
                  href={isHomePage ? "#ventures" : "/#ventures"}
                  className="font-display text-3xl hover:opacity-60 transition-opacity"
                  onClick={handleClose}
                >
                  Ventures
                </a>
                <a
                  href={isHomePage ? "#about" : "/#about"}
                  className="font-display text-3xl hover:opacity-60 transition-opacity"
                  onClick={handleClose}
                >
                  About
                </a>
                <a
                  href={isHomePage ? "#contact" : "/#contact"}
                  className="font-display text-3xl hover:opacity-60 transition-opacity"
                  onClick={handleClose}
                >
                  Contact
                </a>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;